import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { chamarApi, type ErroApi } from '@/nucleo/auth/cliente-api';
import { naoInsistirEmRecusa } from '@/nucleo/naoInsistirEmRecusa';

import type {
  EventoDeAcompanhamento,
  MinhaInscricao,
  StatusDaInscricao,
  TipoPessoa,
} from './contrato';

/**
 * Camada de dados da tela "Minha Inscrição".
 *
 * Lê `GET /bff/v1/portal-credenciado-inscricao/state`
 * (`apps/api/src/modulos/portal-credenciado-inscricao`). Quem decide de quem é a
 * inscrição é o banco, pelo usuário do token (`db/migrations/0127`): a tela não
 * manda identificador nenhum.
 *
 * O cabeçalho `X-Credenciado-Id` não é enviado: o front ainda não tem seletor
 * de cadastro, e sem ele a API usa o cadastro padrão da pessoa.
 *
 * Devolve `null` quando o cadastro não tem inscrição — é o estado vazio. Um
 * 403 (perfil sem acesso ou cadastro sem vínculo) chega como `ErroApi`.
 */
export const CAMINHO_DA_API = '/bff/v1/portal-credenciado-inscricao/state';

export const CHAVES_DA_INSCRICAO = {
  minha: ['credenciado', 'inscricao', 'minha'] as const,
};

/** Uma linha da lista de comprovantes, como a API devolve. */
interface ComprovanteDaApi {
  id: string;
  protocolo: string | null;
  situacao: StatusDaInscricao;
  placa: string | null;
  modalidade: string | null;
  enviada_em: string | null;
}

/** Os dados da inscrição mais recente, como a API devolve. */
interface InscricaoDaApi {
  id: string;
  protocolo: string | null;
  situacao: StatusDaInscricao;
  nome_razao_social: string | null;
  cpf_cnpj: string | null;
  tipo_pessoa: TipoPessoa | null;
  modalidade: string | null;
  dre_nome: string | null;
  email: string | null;
  telefone: string | null;
  enviada_em: string | null;
  analisada_em: string | null;
  parecer: string | null;
  bloqueado_ate: string | null;
  acompanhamento: EventoDeAcompanhamento[];
}

export interface RespostaDaApi {
  state: {
    fase: 'PRONTO' | 'VAZIO';
    inscricoes: ComprovanteDaApi[];
    inscricao: InscricaoDaApi | null;
  };
}

/**
 * Traduz a resposta da API para o formato da tela.
 *
 * Nome, CPF/CNPJ e tipo de cada comprovante vêm da inscrição mais recente: a
 * API lista as inscrições do mesmo CPF/CNPJ, então são os mesmos dados.
 */
export function paraATela({ state }: RespostaDaApi): MinhaInscricao | null {
  const { inscricao, inscricoes } = state;
  if (!inscricao) {
    return null;
  }

  const nome = inscricao.nome_razao_social ?? '';
  const documento = inscricao.cpf_cnpj ?? '';

  return {
    inscricao: {
      id: inscricao.id,
      protocolo: inscricao.protocolo ?? '',
      nome_razao_social: nome,
      cpf_cnpj: documento,
      tipo_pessoa: inscricao.tipo_pessoa,
      modalidade: inscricao.modalidade,
      dre_nome: inscricao.dre_nome,
      email: inscricao.email,
      telefone: inscricao.telefone,
      status_inscricao: inscricao.situacao,
      data_envio: inscricao.enviada_em,
      data_analise: inscricao.analisada_em,
      parecer: inscricao.parecer,
      bloqueado_ate: inscricao.bloqueado_ate,
      acompanhamento: inscricao.acompanhamento,
    },
    comprovantes: inscricoes.map((comprovante) => ({
      id: comprovante.id,
      protocolo: comprovante.protocolo ?? '',
      nome_razao_social: nome,
      cpf_cnpj: documento,
      tipo_pessoa: inscricao.tipo_pessoa,
      modalidade: comprovante.modalidade,
      numero_placa: comprovante.placa,
      data_envio: comprovante.enviada_em,
      status_inscricao: comprovante.situacao,
    })),
  };
}

export function useMinhaInscricao(): UseQueryResult<MinhaInscricao | null, ErroApi> {
  return useQuery<MinhaInscricao | null, ErroApi>({
    queryKey: CHAVES_DA_INSCRICAO.minha,
    queryFn: async ({ signal }) =>
      paraATela(await chamarApi<RespostaDaApi>(CAMINHO_DA_API, { sinal: signal })),
    retry: naoInsistirEmRecusa,
  });
}
